import { useEffect, useMemo, useRef } from 'react';
import { registerRealtime, unregisterRealtime } from '../services/realtimeManager';

/**
 * useRealtimeSubscription — assina bindings de postgres_changes em um canal
 * COMPARTILHADO (multiplexado pelo RealtimeManager), em vez de abrir um canal
 * dedicado por componente.
 *
 * @param {string|null} groupName  Nome do grupo/canal compartilhado (ex.:
 *   `rt:home:${userId}`). `null`/'' desativa a assinatura.
 * @param {Array} bindings  Lista de { event, table, filter?, schema?, handler }.
 * @param {boolean} enabled  Liga/desliga a assinatura.
 *
 * Re-registra APENAS quando os descritores (event/schema/table/filter) mudam;
 * trocar a identidade do `handler` entre renders NÃO reconstrói o canal — o
 * handler corrente é lido via ref no momento do evento.
 */
export function useRealtimeSubscription(groupName, bindings, enabled = true) {
    const bindingsRef = useRef(bindings);
    bindingsRef.current = bindings;

    const descriptorKey = useMemo(() => {
        if (!bindings || bindings.length === 0) return '';
        return bindings
            .map((b) => `${b.event}|${b.schema || 'public'}|${b.table}|${b.filter || ''}`)
            .join(';');
    }, [bindings]);

    useEffect(() => {
        if (!enabled || !groupName || !descriptorKey) return undefined;
        const token = registerRealtime(groupName, bindingsRef);
        return () => unregisterRealtime(groupName, token);
    }, [groupName, descriptorKey, enabled]);
}

export default useRealtimeSubscription;
